import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  Flame, 
  Droplets, 
  Wrench, 
  Search,
  Plus,
  Calendar,
  Shield
} from 'lucide-react';
import { useTranslation } from '../translations';
import { InterventionRequestModal } from './InterventionRequestModal';
import { fetchServicePricing, getCurrentUserCompany, type ServicePricing } from '../lib/supabase';

interface ServiceTile {
  id: string;
  icon: React.ElementType;
  color: string;
  urgentAvailable: boolean;
  name: string;
  description: string;
}

export function DashboardView() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<{ type: string; title: string; isUrgent: boolean } | null>(null);
  const [servicePricing, setServicePricing] = useState<ServicePricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadServicePricing();
  }, []);

  const loadServicePricing = async () => {
    try {
      setIsLoading(true);
      const company = await getCurrentUserCompany();
      if (company) {
        const pricing = await fetchServicePricing(company.id);
        setServicePricing(pricing);
      }
    } catch (error) {
      console.error('Error loading service pricing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Map service pricing to service tiles
  const getServiceTiles = (): ServiceTile[] => {
    // Default icons and colors for known service types
    const defaultIcons: Record<string, { icon: React.ElementType, color: string, urgentAvailable: boolean }> = {
      electrical: { icon: Zap, color: 'from-yellow-400 to-amber-500', urgentAvailable: true },
      heating: { icon: Flame, color: 'from-orange-400 to-red-500', urgentAvailable: true },
      plumbing: { icon: Droplets, color: 'from-blue-400 to-cyan-500', urgentAvailable: true },
      inspection: { icon: Search, color: 'from-green-400 to-emerald-500', urgentAvailable: false },
      safety: { icon: Shield, color: 'from-purple-400 to-indigo-500', urgentAvailable: false },
      other: { icon: Wrench, color: 'from-gray-400 to-gray-500', urgentAvailable: false }
    };

    // If we have service pricing data, use it to create tiles
    if (servicePricing.length > 0) {
      return servicePricing.map(service => {
        const serviceType = service.service_type;
        const defaultConfig = defaultIcons[serviceType] || defaultIcons.other;
        
        return {
          id: serviceType,
          icon: defaultConfig.icon,
          color: defaultConfig.color,
          urgentAvailable: defaultConfig.urgentAvailable,
          name: service.service_name,
          description: service.configuration?.subtitle || 
                      t.dashboard.services[serviceType as keyof typeof t.dashboard.services]?.description || 
                      'Custom service'
        };
      });
    }

    // Fallback to default services if no pricing data
    return Object.entries(defaultIcons).map(([id, config]) => ({
      id,
      icon: config.icon,
      color: config.color,
      urgentAvailable: config.urgentAvailable,
      name: t.dashboard.services[id as keyof typeof t.dashboard.services]?.title || id,
      description: t.dashboard.services[id as keyof typeof t.dashboard.services]?.description || 'Service description'
    }));
  };

  const services = getServiceTiles();

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleServiceClick = (serviceId: string, isUrgent: boolean = false) => {
    const service = services.find(s => s.id === serviceId);
    if (!service) return;
    
    setSelectedService({
      type: serviceId,
      title: service.name,
      isUrgent
    });
    setIsRequestModalOpen(true);
  };

  const handleUrgentRequest = () => {
    // For urgent requests, we'll default to electrical service but allow selection
    setSelectedService({
      type: 'electrical',
      title: 'Urgent Assistance',
      isUrgent: true
    });
    setIsRequestModalOpen(true);
  };

  const handleMultipleServices = () => {
    // For now, we'll handle this as a regular service request
    // In the future, this could open a different modal for multiple services
    setSelectedService({
      type: 'other',
      title: 'Multiple Services',
      isUrgent: false
    });
    setIsRequestModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.dashboard.title}</h1>
          <p className="text-gray-600 mt-1">{t.dashboard.subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder={t.dashboard.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity">
            <Calendar className="h-5 w-5" />
            <span className="hidden md:inline">{t.dashboard.viewCalendar}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-jaimy-red to-jaimy-red-dark p-6 rounded-xl text-white">
          <h2 className="text-lg font-semibold mb-2">{t.dashboard.urgentHelp.title}</h2>
          <p className="text-white/90 mb-4">{t.dashboard.urgentHelp.description}</p>
          <button 
            onClick={handleUrgentRequest}
            className="bg-white text-jaimy-red px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <Zap className="h-5 w-5" />
            {t.dashboard.urgentHelp.button}
          </button>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl text-white">
          <h2 className="text-lg font-semibold mb-2">{t.dashboard.multipleServices.title}</h2>
          <p className="text-white/90 mb-4">{t.dashboard.multipleServices.description}</p>
          <button 
            onClick={handleMultipleServices}
            className="bg-white text-gray-900 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <Plus className="h-5 w-5" />
            {t.dashboard.multipleServices.button}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-white rounded-lg shadow-sm">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No services found</h3>
              <p className="text-gray-600">Try adjusting your search query</p>
            </div>
          ) : (
            filteredServices.map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceClick(service.id)}
                className="group bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 text-left"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${service.color} text-white group-hover:scale-110 transition-transform`}>
                    <service.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-jaimy-red transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {service.description}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Intervention Request Modal */}
      {selectedService && (
        <InterventionRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => {
            setIsRequestModalOpen(false);
            setSelectedService(null);
          }}
          serviceType={selectedService.type}
          serviceTitle={selectedService.title}
          isUrgent={selectedService.isUrgent}
        />
      )}
    </div>
  );
}